// src/components/articles/UserArticleList.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IoDocumentTextOutline, IoPencilOutline, IoTrashOutline, IoCalendarOutline } from 'react-icons/io5';
import Spinner from '../common/Spinner';
import ConfirmationModal from '../common/ConfirmationModal';
import { formatDate } from '../../utils/formatters';
import articleService from '../../services/articleService';
import toastUtil from '../../utils/toastUtil';
import ArticleFilters from './ArticleFilters';

const UserArticleList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    dateRange: { from: '', to: '' }
  });
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    articleId: null,
    articleTitle: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'under review': return 'info';
      case 'submitted': return 'secondary';
      case 'rejected': return 'danger';
      default: return 'info';
    }
  };

  useEffect(() => {
    fetchUserArticles();
  }, []);

  useEffect(() => {
    if (articles.length > 0) {
      applyFiltersAndSearch();
    } else {
      setFilteredArticles([]);
    }
  }, [searchTerm, filters, articles]);

  const fetchUserArticles = async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null); // Clear error state when starting a new fetch
    try {
      const result = await articleService.getUserArticles(page, limit);
      if (result.success) {
        setArticles(result.data.articles);
        setFilteredArticles(result.data.articles);
        setPagination(result.data.pagination);
      } else {
        setError('Failed to fetch articles');
      }
    } catch (err) {
      console.error('Error fetching user articles:', err);
      setError('An error occurred while fetching your articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const applyFiltersAndSearch = () => {
    let results = [...articles];

    // Apply search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      results = results.filter(article =>
        article.title?.toLowerCase().includes(term) ||
        article.abstract?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (filters.status) {
      results = results.filter(article => article.status.toLowerCase() === filters.status.toLowerCase());
    }

    // Apply date range filters
    if (filters.dateRange.from || filters.dateRange.to) {
      results = results.filter(article => {
        const createdDate = new Date(article.createdAt);

        if (filters.dateRange.from && filters.dateRange.to) {
          const fromDate = new Date(filters.dateRange.from);
          const toDate = new Date(filters.dateRange.to);
          // Make toDate inclusive of the whole day
          toDate.setHours(23, 59, 59, 999);
          return createdDate >= fromDate && createdDate <= toDate;
        }

        if (filters.dateRange.from) {
          const fromDate = new Date(filters.dateRange.from);
          return createdDate >= fromDate;
        }

        if (filters.dateRange.to) {
          const toDate = new Date(filters.dateRange.to);
          // Make toDate inclusive of the whole day
          toDate.setHours(23, 59, 59, 999);
          return createdDate <= toDate;
        }

        return true;
      });
    }

    setFilteredArticles(results);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchUserArticles(newPage, pagination.limit);
  };

  const handleViewArticle = (articleId) => {
    navigate(`/articles/${articleId}`);
  };

  const handleEditArticle = (articleId) => {
    navigate(`/articles/${articleId}/edit`, {
      state: { referrer: '/articles' }
    });
  };

  const handleDeleteArticle = (article) => {
    setDeleteModal({
      isOpen: true,
      articleId: article._id,
      articleTitle: article.title
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      articleId: null,
      articleTitle: ''
    });
  };

  const confirmDelete = async () => {
    if (!deleteModal.articleId || deleteLoading) return;

    setDeleteLoading(true);

    try {
      const result = await articleService.deleteArticle(deleteModal.articleId);

      if (result.success) {
        // Remove the article from the state
        const updatedArticles = articles.filter(article => article._id !== deleteModal.articleId);
        setArticles(updatedArticles);

        // Show success message
        toastUtil.success(result.message || 'Article deleted successfully');
      } else {
        toastUtil.error(result.message || 'Failed to delete article');
      }
    } catch (error) {
      toastUtil.error('An error occurred while deleting the article');
      console.error('Delete article error:', error);
    } finally {
      setDeleteLoading(false);
      closeDeleteModal();
    }
  };

  const StatusPill = ({ status }) => {
    const key = getStatusColor(status);
    const map = {
      success: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/40',
      warning: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900/40',
      info: 'bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40',
      secondary: 'bg-slate-50 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:ring-slate-700/60',
      danger: 'bg-red-50 text-red-700 ring-1 ring-red-100 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-900/40',
    };

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${map[key] || map.info}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="user-article-list">
      <div className="mx-auto w-full max-w-6xl">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                My Submitted Articles
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Track your submissions, filter by status/date, and manage drafts.
              </p>
            </div>

            <div className="w-full sm:w-auto">
              <ArticleFilters
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-14">
              <Spinner size="medium" />
            </div>
          ) : error ? (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
              <p className="text-sm font-medium">{error}</p>
              <button
                className="mt-4 inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                onClick={() => fetchUserArticles()}
              >
                Try Again
              </button>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="mt-8 rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center dark:border-slate-800 dark:bg-slate-950/40">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
                <IoDocumentTextOutline className="text-2xl" />
              </div>
              <h3 className="mt-4 text-base font-bold text-slate-900 dark:text-white">
                No Articles Found
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {searchTerm.trim() || filters.status || filters.dateRange.from || filters.dateRange.to
                  ? "No articles match your search criteria."
                  : "You haven't submitted any articles yet."}
              </p>
              <Link
                to="/add-article"
                className="mt-6 inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                Submit New Article
              </Link>
            </div>
          ) : (
            <>
              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] border-collapse">
                    <thead className="bg-slate-50 dark:bg-slate-950/40">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Title
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Submission Date
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Status
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {filteredArticles.map((article) => (
                        <tr
                          key={article._id}
                          onClick={() => handleViewArticle(article._id)}
                          className="group cursor-pointer transition hover:bg-slate-50 dark:hover:bg-slate-800/40"
                        >
                          <td className="px-5 py-4 align-top">
                            <div className="min-w-0 max-w-[520px]">
                              <div className="article-title whitespace-normal break-words text-sm font-semibold text-slate-900 dark:text-white">
                                {article.title}
                              </div>

                              {article.abstract && (
                                <div className="article-abstract mt-1 whitespace-normal break-words text-sm text-slate-600 dark:text-slate-300">
                                  {article.abstract}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-4 align-top">
                            <div className="meta-date inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                <IoCalendarOutline />
                              </span>
                              <span className="font-medium">{formatDate(article.createdAt)}</span>
                            </div>
                          </td>

                          <td className="px-5 py-4 align-top">
                            <StatusPill status={article.status} />
                          </td>

                          <td
                            className="px-5 py-4 align-top"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="article-actions flex items-center gap-2">
                              <button
                                className="action-button view inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60 dark:hover:text-white"
                                title="View Article"
                                onClick={() => handleViewArticle(article._id)}
                                type="button"
                                aria-label="View article"
                              >
                                <IoDocumentTextOutline />
                              </button>

                              {article.status !== 'approved' && (
                                <button
                                  className="action-button edit inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60 dark:hover:text-white"
                                  title="Edit Article"
                                  onClick={() => handleEditArticle(article._id)}
                                  type="button"
                                  aria-label="Edit article"
                                >
                                  <IoPencilOutline />
                                </button>
                              )}

                              {article.status !== 'approved' && (
                                <button
                                  className="action-button delete inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-700 shadow-sm transition hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/30"
                                  title="Delete Article"
                                  onClick={() => handleDeleteArticle(article)}
                                  type="button"
                                  aria-label="Delete article"
                                >
                                  <IoTrashOutline />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination mt-6 flex flex-col items-center justify-between gap-3 sm:flex-row">
                  <button
                    className="pagination-button inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    type="button"
                  >
                    Previous
                  </button>

                  <span className="pagination-info text-sm font-medium text-slate-600 dark:text-slate-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>

                  <button
                    className="pagination-button inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDelete}
                title="Delete Article"
                message={`Are you sure you want to delete "${deleteModal.articleTitle}"? This action cannot be undone.`}
                confirmText={deleteLoading ? "Deleting..." : "Delete"}
                cancelText="Cancel"
                type="danger"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserArticleList;
